import { isBrowser } from '@noths/polaris-client-utils';
import bunyan from 'bunyan';
import type { RedisClientType } from 'redis';
import { createClient } from 'redis';

import { CACHE_STATUS_EXPIRED, CACHE_STATUS_FRESH, RECONNECTION_RETRY_TIME } from './constants';
import type { CacheEntry } from './types';

export const MAX_RETRIES = 5;

const logger = isBrowser()
  ? (console as unknown as bunyan)
  : bunyan.createLogger({
      name: process.env.DD_SERVICE_NAME || 'unknown',
    });

let cacheClient: RedisClientType | null = null;
let isCacheReady = false;

export const setIsCacheReady = (readyState: boolean) => (isCacheReady = readyState);

const getByKey = async (cacheKey: string, cacheTTLInSeconds = 300): Promise<CacheEntry | null> => {
  if (!isCacheReady) {
    return null;
  }

  try {
    const cacheEntry = await cacheClient?.get(cacheKey);

    if (!cacheEntry) {
      return null;
    }

    const { data, updated } = JSON.parse(cacheEntry);
    const cacheTTLInMilliseconds = cacheTTLInSeconds * 1000;

    const status =
      updated + cacheTTLInMilliseconds < Date.now() ? CACHE_STATUS_EXPIRED : CACHE_STATUS_FRESH;

    return {
      data,
      updated,
      status,
    };
  } catch {
    return null;
  }
};

const setByKey = async (cacheKey: string, data: unknown) => {
  if (!isCacheReady) {
    return;
  }

  await cacheClient?.set(
    cacheKey,
    JSON.stringify({
      data,
      updated: Date.now(),
    }),
  );
};

export const reconnectStrategy = (retries: number) => {
  logger.info(`Trying to reconnect to Redis: Attempt #${retries + 1}`);

  if (retries <= MAX_RETRIES) {
    return retries * 50;
  }

  return RECONNECTION_RETRY_TIME;
};

/* istanbul ignore next */
export const createCacheClient = () => {
  if (!(Boolean(process.env.REDIS_HOST) && Boolean(process.env.REDIS_AUTH_TOKEN))) {
    console.warn(
      'REDIS_HOST and REDIS_AUTH_TOKEN must be set in the environment in order to use caching',
    );
    return;
  }

  if (cacheClient) {
    return {
      getByKey,
      setByKey,
    };
  }

  cacheClient = createClient({
    socket: {
      host: process.env.REDIS_HOST,
      tls: process.env.REDIS_HOST !== 'localhost',
      reconnectStrategy,
    },
    password: process.env.REDIS_AUTH_TOKEN,
  }) as RedisClientType;

  cacheClient.connect();

  cacheClient.on('error', (error) => {
    logger.warn('Redis client error', error);
  });

  cacheClient.on('connect', () => {
    logger.info('Connecting Redis client');
  });

  cacheClient.on('ready', () => {
    logger.info('Redis client ready');
    setIsCacheReady(true);
  });

  cacheClient.on('end', () => {
    logger.warn('Redis client disconnected');
    setIsCacheReady(false);
  });

  cacheClient.on('reconnecting', () => {
    logger.info('Reconnecting Redis client');
  });

  return {
    getByKey,
    setByKey,
  };
};
